// /home/vitruvio/puntum-frontend/src/context/AuthContext.jsx

import React, { createContext, useContext, useState, useEffect } from 'react'
import { apiFetch } from '../services/api'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem('puntum_user') || localStorage.getItem('user')
      return raw ? JSON.parse(raw) : null
    } catch (e) {
      return null
    }
  })

  const [sucursales, setSucursales] = useState([])
  const [sucursalActiva, setSucursalActiva] = useState(() => {
    try {
      const raw = localStorage.getItem('puntum_sucursal_activa')
      return raw ? JSON.parse(raw) : null
    } catch (e) {
      return null
    }
  })

  const [loading, setLoading] = useState(true)

  useEffect(() => {
    inicializarSesion()
  }, [])

  const inicializarSesion = async () => {
    const token = localStorage.getItem('puntum_token')
    if (!token) {
      setLoading(false)
      return
    }

    try {
      // 1. Cargar Perfil de usuario actualizado desde API o fallback local
      let userData = await apiFetch('/auth/me').catch(() => null)
      
      if (!userData) {
        // Fallback al usuario guardado en localStorage
        const rawLocal = localStorage.getItem('puntum_user')
        if (rawLocal) userData = JSON.parse(rawLocal)
      }

      if (userData) {
        // Normalizar fallback de días de trial y datos de empresa
        if (userData.trial_days_left === undefined) userData.trial_days_left = 30
        if (userData.needs_company_update === undefined) userData.needs_company_update = true

        setUser(userData)
        localStorage.setItem('puntum_user', JSON.stringify(userData))
      }

      // 2. Cargar Sucursales desde Backend
      let listaSucursales = await apiFetch('/sucursales').catch(() => [])
      if (!Array.isArray(listaSucursales)) listaSucursales = []

      // 3. Fallback/Auto-creación de "Casa Matriz"
      if (listaSucursales.length === 0) {
        try {
          const nuevaMatriz = await apiFetch('/sucursales', {
            method: 'POST',
            body: JSON.stringify({
              nombre: 'Casa Matriz',
              direccion: userData?.direccion || 'Dirección Principal',
              es_principal: true,
              activa: true
            })
          }).catch(() => null)

          if (nuevaMatriz && nuevaMatriz.id) {
            listaSucursales = [nuevaMatriz]
          } else {
            listaSucursales = [{ id: 'matriz-01', nombre: 'Casa Matriz', es_principal: true, activa: true }]
          }
        } catch (err) {
          listaSucursales = [{ id: 'matriz-01', nombre: 'Casa Matriz', es_principal: true, activa: true }]
        }
      }

      setSucursales(listaSucursales)

      // 4. Auto-seleccionar sucursal activa
      const guardada = localStorage.getItem('puntum_sucursal_activa')
      let activa = guardada ? JSON.parse(guardada) : null

      if (!activa || !listaSucursales.some(s => String(s.id) === String(activa.id))) {
        activa = listaSucursales.find(s => s.es_principal) || listaSucursales[0]
      }

      if (activa) {
        setSucursalActiva(activa)
        localStorage.setItem('puntum_sucursal_activa', JSON.stringify(activa))
      }
    } catch (err) {
      console.warn('⚠️ Error inicializando sesión:', err)
    } finally {
      setLoading(false)
    }
  }

  // 🔄 Guardar la respuesta del login/registro directamente
  const loginUser = (userData, token) => {
    if (token) localStorage.setItem('puntum_token', token)
    if (userData) {
      if (userData.trial_days_left === undefined) userData.trial_days_left = 30
      if (userData.needs_company_update === undefined) userData.needs_company_update = true

      localStorage.setItem('puntum_user', JSON.stringify(userData))
      setUser(userData)
    }
    inicializarSesion()
  }

  const cambiarSucursalActiva = (sucursal) => {
    setSucursalActiva(sucursal)
    localStorage.setItem('puntum_sucursal_activa', JSON.stringify(sucursal))
  }

  const getLimite = (clave) => {
    const plan = (user?.plan || 'pro trial').toLowerCase()
    const limites = {
      emprendedor: { max_sucursales: 1, max_usuarios: 2, max_productos: 500 },
      'pro trial': { max_sucursales: 5, max_usuarios: 10, max_productos: 999999 },
      pro: { max_sucursales: 5, max_usuarios: 10, max_productos: 999999 },
      enterprise: { max_sucursales: 999, max_usuarios: 999, max_productos: 999999 }
    }
    const config = limites[plan] || limites.pro
    return config[clave] ?? 5
  }

  const logout = () => {
    localStorage.removeItem('puntum_token')
    localStorage.removeItem('puntum_user')
    localStorage.removeItem('user')
    localStorage.removeItem('puntum_sucursal_activa')
    setUser(null)
    setSucursales([])
    setSucursalActiva(null)
    window.location.reload()
  }

  const value = {
    user,
    perfil: user,
    setUser,
    sucursales,
    sucursalActiva,
    cambiarSucursalActiva,
    getLimite,
    loading,
    loginUser,
    logout,
    refrescarSesion: inicializarSesion
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => useContext(AuthContext) || {}